// Select — 1:1 port of legacy `src/ui/Primitives.jsx:104–126`.
import { useRef, useState, type CSSProperties } from 'react';
import { useFormRowId } from './FormRow.js';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange?: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  style?: CSSProperties;
  disabled?: boolean;
}

export function Select({ value, onChange, options, placeholder, style, disabled }: SelectProps) {
  const [focus, setFocus] = useState(false);
  const blurAfterPointerChangeRef = useRef(false);
  const rowId = useFormRowId();
  return (
    <select
      id={rowId}
      value={value ?? ''}
      disabled={disabled}
      onPointerDown={() => {
        blurAfterPointerChangeRef.current = true;
      }}
      onKeyDown={() => {
        blurAfterPointerChangeRef.current = false;
      }}
      onChange={(e) => {
        onChange?.(e.target.value);
        if (blurAfterPointerChangeRef.current) {
          blurAfterPointerChangeRef.current = false;
          e.currentTarget.blur();
        }
      }}
      onFocus={() => setFocus(true)}
      onBlur={() => {
        blurAfterPointerChangeRef.current = false;
        setFocus(false);
      }}
      style={{
        height: 'var(--control-h)',
        padding: '0 28px 0 10px',
        borderRadius: 'var(--r-2)',
        border: `1px solid ${focus ? 'var(--accent-1)' : 'var(--border-1)'}`,
        boxShadow: focus ? '0 0 0 3px var(--accent-ring)' : 'none',
        background: 'var(--bg-1)',
        color: 'var(--fg-1)',
        fontFamily: 'var(--font-ui)',
        fontSize: 13,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
        transition:
          'border-color var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out)',
        ...style,
      }}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
